import { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import PieveWordmark from '@/components/surfaces/PieveWordmark.jsx';
import PIEVE_ONBOARDING_FINISHED_PREF_KEY from '@/lib/prefs/PIEVE_ONBOARDING_FINISHED_PREF_KEY.js';
import appendOnboardingIntroChatMessage from '@/redux/actions/onboardingActions/appendOnboardingIntroChatMessage.js';
import applyOnboardingIntroChatResponse from '@/redux/actions/onboardingActions/applyOnboardingIntroChatResponse.js';
import resetOnboardingIntroChat from '@/redux/actions/onboardingActions/resetOnboardingIntroChat.js';
import postOnboardingIntroChatMessage from '@/redux/requests/onboarding/postOnboardingIntroChatMessage.js';
import { setPref } from '@/redux/requests/prefsRequests.js';
import { isOnboardingIntroChatComplete, onboardingIntroChat } from '@/redux/selectors/onboardingSelectors.js';
import OnboardingMessage from './components/OnboardingMessage.jsx';
import OnboardingReadyCard from './components/OnboardingReadyCard.jsx';
import launchOnboardingThread from './lib/launchOnboardingThread.js';
import mapOnboardingIntroChatMessagesForApi from './lib/mapOnboardingIntroChatMessagesForApi.js';
import './styles/onboarding.css';

const SKIP_HANDOFF_DELAY_MS = 1200;

/**
 * First-run setup: a short conversation where Pieve learns the person's name and role (the API asks
 * one question per turn), then opens their onboarding conversation. Skip ends setup without it.
 */
const OnboardingScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const introChat = useSelector(onboardingIntroChat);
  const isComplete = useSelector(isOnboardingIntroChatComplete);
  const [draft, setDraft] = useState('');
  const [isWaiting, setIsWaiting] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const messages = Array.isArray(introChat.messages) ? introChat.messages : [];
  const isReady = isComplete && !introChat.shouldSkipOnboarding;

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages.length, isWaiting, isReady]);

  const finishWithoutThread = async () => {
    setError('');
    const res = await setPref({ path: PIEVE_ONBOARDING_FINISHED_PREF_KEY, value: true });

    if (res?.success === false) {
      setError(res.message || 'Setup could not be closed. Try again.');
      return;
    }

    dispatch(resetOnboardingIntroChat());
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (!introChat.shouldSkipOnboarding) {
      return undefined;
    }
    const timer = setTimeout(finishWithoutThread, SKIP_HANDOFF_DELAY_MS);
    return () => clearTimeout(timer);
    // The handoff runs once, when the API classifies the answer as a request to skip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introChat.shouldSkipOnboarding]);

  const handleSend = async (event) => {
    event.preventDefault();
    const content = draft.trim();

    if (!content || isWaiting) {
      return;
    }

    setError('');
    setDraft('');
    setIsWaiting(true);
    const userMessage = { id: `user-${Date.now()}`, role: 'user', content };
    dispatch(appendOnboardingIntroChatMessage(userMessage));
    const res = await postOnboardingIntroChatMessage({ messages: mapOnboardingIntroChatMessagesForApi([...messages, userMessage]) });
    setIsWaiting(false);

    if (res?.success === false) {
      setError(res.message || 'Pieve could not answer. Send your message again.');
      return;
    }

    const data = res?.data || {};
    dispatch(
      applyOnboardingIntroChatResponse({
        message: { id: `assistant-${Date.now()}`, content: data.responseMessage },
        name: data.name,
        job: data.job,
        jobFamily: data.jobFamily,
        isDevRole: data.isDevRole,
        shouldSkipOnboarding: data.shouldSkipOnboarding
      })
    );
  };

  const handleLaunch = async () => {
    setError('');
    setIsLaunching(true);
    const res = await dispatch(launchOnboardingThread({ introChat }));

    if (!res?.success) {
      setIsLaunching(false);
      setError(res?.message || 'Your first conversation could not be opened. Try again.');
      return;
    }

    dispatch(resetOnboardingIntroChat());
    navigate(`/threads/${encodeURIComponent(res.threadId)}`, { replace: true });
  };

  return (
    <div className="onboarding">
      <header className="onboarding__bar">
        <PieveWordmark height={24} />
        <Button variant="ghost" onClick={finishWithoutThread} disabled={isLaunching}>
          Skip setup
        </Button>
      </header>
      <main className="onboarding__main">
        <div className="onboarding__intro">
          <h1 className="onboarding__title">Let&apos;s set up Pieve for your work</h1>
          <p className="onboarding__lead">Two quick questions. Pieve uses your answers to suggest what to automate first.</p>
        </div>
        <ol className="onboarding__conversation" aria-live="polite">
          {messages.map((message) => (
            <OnboardingMessage key={message.id} speaker={message.role}>
              {message.content}
            </OnboardingMessage>
          ))}
          {isWaiting && <OnboardingMessage speaker="assistant" isPending />}
        </ol>
        {isReady ? (
          <OnboardingReadyCard name={introChat.name} isLaunching={isLaunching} onLaunch={handleLaunch} />
        ) : (
          !introChat.shouldSkipOnboarding && (
            <form className="onboarding__composer" onSubmit={handleSend}>
              <label className="onboarding__composer-label" htmlFor="onboarding-answer">
                Your answer
              </label>
              <input id="onboarding-answer" className="onboarding__composer-input" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Type your answer" autoComplete="off" disabled={isWaiting} />
              <Button type="submit" variant="primary" iconOnly iconStart={ArrowUp} label="Send answer" disabled={!draft.trim() || isWaiting} />
            </form>
          )
        )}
        {error && (
          <p className="onboarding__error" role="alert">
            {error}
          </p>
        )}
        <div ref={endRef} />
      </main>
    </div>
  );
};

export default OnboardingScreen;
